import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useInstallments } from '../hooks/useInstallments';
import { api } from '../services/api';
import { getLocalDateInputValue } from '../utils/dateUtils';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const getSignedQuantity = (movement) => {
  const value = Number(movement.quantity) || 0;
  return movement.type === 'Saída' ? -Math.abs(value) : Math.abs(value);
};

const getMovementKey = (movement) => movement._id || movement.id || `${movement.date}-${movement.lot}-${movement.quantity}`;

const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const parseAmountToNumber = (value = '') => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const normalized = value.replace(/\./g, '').replace(',', '.');
  return parseFloat(normalized) || 0;
};

const formatCurrencyInput = (value = '') => {
  const digits = value.replace(/\D/g, '');
  const number = parseFloat(digits) / 100 || 0;
  return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normalizeDate = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day).toISOString();
};

const defaultMovement = {
  type: 'Entrada',
  quantity: '',
  patient: '',
  patientId: '',
  note: '',
  expiry: '',
  lot: '',
};

const defaultPaymentData = {
  amount: '0,00',
  date: formatDateForInput(new Date()),
  paymentStatus: 'pendente',
  paymentMethod: 'dinheiro',
  hasInstallments: false,
  installments: 1,
  installmentPaymentMethod: 'cartao_credito',
  notes: '',
};

export default function MedicamentoDetalhes() {
  const { currentUser, patients = [] } = useOutletContext();
  const navigate = useNavigate();
  const { state } = useLocation();
  const params = useParams();
  const medicationId = params.id || state?.medication?._id || state?.medicationId || '';

  const [medication, setMedication] = useState(state?.medication || null);
  const [movements, setMovements] = useState(state?.movements || []);
  const [isArchived, setIsArchived] = useState(state?.medication?.archived || false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState(defaultMovement);
  const [patientQuery, setPatientQuery] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [addPayment, setAddPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(defaultPaymentData);
  const [visibleRows, setVisibleRows] = useState(50);
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [editingMovementId, setEditingMovementId] = useState(null);

  const installmentsFormData = addPayment ? paymentData : { ...paymentData, hasInstallments: false };
  const {
    installmentSchedule,
    handleInstallmentFieldChange,
    recalculateInstallments,
    unlockSchedule,
  } = useInstallments(installmentsFormData, parseAmountToNumber, formatDateForInput);
  const scheduleCount =
    installmentsFormData.hasInstallments && (installmentsFormData.installments || installmentSchedule.length)
      ? installmentsFormData.installments || installmentSchedule.length
      : installmentSchedule.length || 1;

  useEffect(() => {
    if (!medicationId) return;
    fetchMedication(medicationId);
    fetchMovements(medicationId);
  }, [medicationId]);

  const fetchMedication = async (id) => {
    try {
      const { data } = await api.get(`/inventory/medications/${id}`);
      setMedication(data);
      setIsArchived(data.archived);
    } catch (error) {
      console.error('Erro ao carregar medicamento', error);
    }
  };

  const fetchMovements = async (id) => {
    try {
      setLoadingMovements(true);
      const { data } = await api.get(`/inventory/medications/${id}/movements`);
      setMovements(data);
    } catch (error) {
      console.error('Erro ao carregar movimentações', error);
    } finally {
      setLoadingMovements(false);
    }
  };

  const movementTotals = useMemo(() => {
    const totals = {};
    let runningTotal = 0;
    const chronological = [...movements].sort((a, b) => new Date(a.date) - new Date(b.date));
    chronological.forEach((movement) => {
      runningTotal += getSignedQuantity(movement);
      totals[getMovementKey(movement)] = runningTotal;
    });
    return totals;
  }, [movements]);

  const orderedMovements = useMemo(() => {
    const { field, direction } = sortConfig;
    return [...movements].sort((a, b) => {
      const getValue = (item, key) => {
        if (key === 'date' || key === 'expiry') {
          return new Date(item[key] || 0).getTime();
        }
        if (key === 'quantity') return Number(item[key]) || 0;
        return (item[key] || '').toString().toLowerCase();
      };
      const aValue = getValue(a, field);
      const bValue = getValue(b, field);
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [movements, sortConfig]);

  const totalAvailable = medication?.totalQuantity || 0;

  const nearestExpiryInfo = useMemo(() => {
    const dates = [];
    if (medication?.lastExpiry) {
      dates.push(new Date(medication.lastExpiry));
    }
    movements.forEach((movement) => {
      if (movement.expiry) {
        const parsed = new Date(movement.expiry);
        if (!Number.isNaN(parsed.getTime())) {
          dates.push(parsed);
        }
      }
    });
    if (dates.length === 0) {
      return { date: null, isSoon: false };
    }
    const nearest = dates.sort((a, b) => a - b)[0];
    const diffInDays = (nearest.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return { date: nearest, isSoon: diffInDays <= 60 };
  }, [medication?.lastExpiry, movements]);

  const handleAdjustmentChange = (event) => {
    const { name, value } = event.target;
    if (name === 'type') {
      setAdjustmentData((prev) => ({ ...prev, type: value, patient: '' }));
      setPatientQuery('');
      setShowPatientDropdown(false);
      if (value === 'Saída') {
        setAddPayment(false);
      }
      return;
    }
    if (name === 'patient') {
      setAdjustmentData((prev) => ({ ...prev, patient: value, patientId: prev.type === 'Saída' ? prev.patientId : '' }));
      return;
    }
    setAdjustmentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAdjustment = async (event) => {
    event.preventDefault();
    const quantity = Number(adjustmentData.quantity);
    const isExitMovement = adjustmentData.type === 'Saída';
    if (!quantity || Number.isNaN(quantity) || !medicationId) {
      return;
    }
    if (!adjustmentData.patient.trim()) {
      alert(isExitMovement ? 'Selecione um paciente.' : 'Informe o fornecedor.');
      return;
    }
    if (isExitMovement && !adjustmentData.patientId) {
      alert('Selecione um paciente válido.');
      return;
    }
    if (addPayment) {
      const paymentAmount = parseAmountToNumber(paymentData.amount);
      if (!paymentAmount || Number.isNaN(paymentAmount)) {
        alert('Informe o valor do pagamento.');
        return;
      }
    }
    if (!adjustmentData.lot.trim() || !adjustmentData.expiry) {
      alert('Informe lote e validade.');
      return;
    }
    try {
      const payload = {
        type: adjustmentData.type,
        quantity: Math.abs(quantity),
        lot: adjustmentData.lot.trim(),
        expiry: adjustmentData.expiry || null,
        patient: adjustmentData.patient.trim() || (adjustmentData.type === 'Entrada' ? 'Entrada manual' : 'Paciente não informado'),
        note: adjustmentData.note.trim(),
        date: editingMovementId ? undefined : getLocalDateInputValue(), // Don't change date on edit
      };

      let movementResult;
      if (editingMovementId) {
        const { data } = await api.put(`/inventory/movements/${editingMovementId}`, payload);
        movementResult = data;
      } else {
        const { data } = await api.post('/inventory/movements', { medicationId, ...payload });
        movementResult = data;
      }

      if (!editingMovementId && addPayment && adjustmentData.type === 'Entrada') {
        await registerPayment(movementResult.movement._id, movementResult.medication || medication);
      }

      setAdjustmentData(defaultMovement);
      setPaymentData(defaultPaymentData);
      setAddPayment(false);
      setPatientQuery('');
      setShowPatientDropdown(false);
      setShowAdjustmentModal(false);
      setEditingMovementId(null);
      setVisibleRows(50);
      await fetchMedication(medicationId);
      await fetchMovements(medicationId);
    } catch (error) {
      console.error('Erro ao registrar movimentação', error);
    }
  };

  const registerPayment = async (movementId, medicationSnapshot) => {
    const amount = parseAmountToNumber(paymentData.amount);
    if (amount <= 0) {
      throw new Error('Valor do pagamento deve ser maior que zero');
    }
    const description = `${medicationSnapshot?.laboratory || 'Fornecedor'} - ${medicationSnapshot?.name || medication?.name || 'Medicamento'}`;
    const notes = [
      paymentData.notes,
      adjustmentData.patient ? `${adjustmentData.type === 'Saída' ? 'Paciente' : 'Fornecedor'}: ${adjustmentData.patient}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    const payload = {
      type: 'despesa',
      category: 'Produto',
      description,
      amount,
      date: normalizeDate(paymentData.date),
      paymentStatus: paymentData.paymentStatus,
      paymentMethod: paymentData.paymentMethod,
      hasInstallments: paymentData.hasInstallments,
      installments: paymentData.hasInstallments ? Number(paymentData.installments) || 1 : 1,
      installmentPaymentMethod: paymentData.installmentPaymentMethod,
      relatedTo: movementId,
      notes,
    };

    if (adjustmentData.patientId) {
      payload.patientId = adjustmentData.patientId;
    }

    if (adjustmentData.type === 'Entrada' && adjustmentData.patient) {
      payload.supplier = adjustmentData.patient;
    }

    if (paymentData.hasInstallments) {
      if (installmentSchedule.length === 0) {
        throw new Error('Defina as parcelas antes de registrar o pagamento.');
      }
      const totalInstallmentsValue = installmentSchedule.reduce(
        (sum, inst) => sum + parseAmountToNumber(inst.amount),
        0
      );
      const difference = amount - totalInstallmentsValue;
      if (Math.abs(difference) > 0.01) {
        throw new Error(`A soma das parcelas deve ser igual ao valor total. Diferença: ${formatCurrency(difference)}`);
      }
      payload.customInstallments = installmentSchedule.map((inst, index) => ({
        amount: parseAmountToNumber(inst.amount),
        paymentMethod: inst.paymentMethod,
        date: normalizeDate(inst.date),
        installmentNumber: inst.installmentNumber || index + 1,
        paymentStatus: inst.paymentStatus || 'pendente',
      }));
    }

    await api.post('/financial', payload);
  };

  const handleSoftDelete = async () => {
    if (!medicationId) return;
    const confirmArchive = window.confirm('Deseja realmente arquivar este medicamento?');
    if (!confirmArchive) return;
    try {
      const { data } = await api.put(`/inventory/medications/${medicationId}`, { archived: true });
      setMedication(data);
      setIsArchived(true);
    } catch (error) {
      console.error('Erro ao arquivar medicamento', error);
    }
  };

  const handleRestore = async () => {
    if (!medicationId) return;
    try {
      const { data } = await api.put(`/inventory/medications/${medicationId}`, { archived: false });
      setMedication(data);
      setIsArchived(false);
    } catch (error) {
      console.error('Erro ao restaurar medicamento', error);
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
  };

  const handleEditMovement = (movement) => {
    setAdjustmentData({
      type: movement.type,
      quantity: movement.quantity.toString(),
      patient: movement.patient || '',
      patientId: movement.patientId || '',
      note: movement.note || '',
      expiry: movement.expiry ? formatDateForInput(movement.expiry) : '',
      lot: movement.lot || '',
    });
    setPatientQuery(movement.patient || '');
    setEditingMovementId(movement._id);
    setShowAdjustmentModal(true);
  };

  const handleDeleteMovement = async (movementId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta movimentação?')) return;
    try {
      await api.delete(`/inventory/movements/${movementId}`);
      setMovements(movements.filter(m => m._id !== movementId));
      // Refresh medication
      fetchMedication(medicationId);
    } catch (error) {
      console.error('Erro ao excluir movimentação:', error);
      alert('Erro ao excluir movimentação');
    }
  };

  const handlePaymentChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (type === 'checkbox') {
      setPaymentData((prev) => ({ ...prev, [name]: checked }));
      if (name === 'hasInstallments') {
        unlockSchedule();
      }
      return;
    }
    let parsedValue = value;
    if (name === 'amount') {
      parsedValue = formatCurrencyInput(value);
    } else if (name === 'installments') {
      parsedValue = Number(value);
    }
    setPaymentData((prev) => ({ ...prev, [name]: parsedValue }));
    if (['amount', 'date', 'installments', 'installmentPaymentMethod'].includes(name)) {
      unlockSchedule();
    }
  };

  if (!medicationId) {
    return (
      <div className="inventory-card">
        <p className="helper-text">Medicamento não encontrado.</p>
      </div>
    );
  }

  const displayedMovements = orderedMovements.slice(0, visibleRows);
  const isExitMovement = adjustmentData.type === 'Saída';

  const closeAdjustmentModal = () => {
    setShowAdjustmentModal(false);
    setAdjustmentData(defaultMovement);
    setPatientQuery('');
    setShowPatientDropdown(false);
    setAddPayment(false);
    setPaymentData(defaultPaymentData);
    setEditingMovementId(null);
  };

  return (
    <div className="inventory-card medication-detail">
      <div className="medication-detail-toolbar">
        <div>
          <h1>{medication?.name || 'Medicamento'}</h1>
          {currentUser && <p className="welcome-text">Visualizando detalhamento para {currentUser}.</p>}
        </div>
        <div className="medication-detail-toolbar-actions">
          <button className="mac-button" type="button" onClick={() => setShowAdjustmentModal(true)} disabled={isArchived}>
            + Nova movimentação
          </button>
        </div>
      </div>

      {isArchived && (
        <div className="archived-banner">
          Este medicamento está arquivado. Novos ajustes ficarão indisponíveis até a restauração.
        </div>
      )}

      <div className="movement-summary">
        <div>
          <span>Total disponível</span>
          <strong>{totalAvailable}</strong>
        </div>
        <div>
          <span>Estoque mínimo</span>
          <strong>{medication?.min ?? 0}</strong>
        </div>
        <div className={`validity-card ${nearestExpiryInfo.isSoon ? 'warning' : ''}`}>
          <span>Validade mais próxima</span>
          <strong>{nearestExpiryInfo.date ? formatDate(nearestExpiryInfo.date) : '—'}</strong>
          {nearestExpiryInfo.isSoon && <small>Vencendo em até 60 dias</small>}
        </div>
      </div>

      <div className="medication-info-card">
        <div>
          <span>Categoria</span>
          <strong>{medication?.category || '—'}</strong>
        </div>
        <div>
          <span>Laboratório</span>
          <strong>{medication?.laboratory || '—'}</strong>
        </div>
        <div>
          <span>Unidade padrão</span>
          <strong>{medication?.unit || 'unid.'}</strong>
        </div>
      </div>

      <div className="movements-table">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('date')} className="sortable">
                Data
              </th>
              <th onClick={() => handleSort('patient')} className="sortable">
                Origem/Paciente
              </th>
              <th>Tipo</th>
              <th onClick={() => handleSort('lot')} className="sortable">
                Lote
              </th>
              <th onClick={() => handleSort('quantity')} className="sortable">
                Quantidade
              </th>
              <th onClick={() => handleSort('expiry')} className="sortable">
                Validade
              </th>
              <th>Observação</th>
              <th>Estoque após</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loadingMovements ? (
              <tr>
                <td colSpan="9" className="inventory-empty">
                  Carregando movimentações...
                </td>
              </tr>
            ) : displayedMovements.length === 0 ? (
              <tr>
                <td colSpan="9" className="inventory-empty">
                  Nenhum movimento registrado para este medicamento.
                </td>
              </tr>
            ) : (
              displayedMovements.map((movement) => {
                const movementKey = getMovementKey(movement);
                return (
                  <tr key={movementKey}>
                    <td>{formatDate(movement.date)}</td>
                    <td>{movement.patient || '—'}</td>
                    <td>
                      <span className={`movement-type ${movement.type === 'Saída' ? 'out' : 'in'}`}>{movement.type}</span>
                    </td>
                    <td>{movement.lot || '—'}</td>
                    <td className={movement.type === 'Saída' ? 'movement-value out' : 'movement-value in'}>
                      {movement.type === 'Saída' ? '-' : '+'}
                      {movement.quantity} {medication?.unit || 'unid.'}
                    </td>
                    <td>{formatDate(movement.expiry)}</td>
                    <td>{movement.note || '—'}</td>
                    <td>{movementTotals[movementKey] ?? '—'}</td>
                    <td>
                      <button className="ghost-button" onClick={() => handleEditMovement(movement)} disabled={isArchived}>
                        Editar
                      </button>
                      <button className="ghost-button ghost-danger" onClick={() => handleDeleteMovement(movement._id)} disabled={isArchived}>
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {orderedMovements.length > visibleRows && (
          <div className="table-footer">
            <button className="ghost-button" type="button" onClick={() => setVisibleRows((prev) => prev + 50)}>
              Mostrar mais
            </button>
          </div>
        )}
      </div>

      <div className="medication-detail-footer">
        <button className="ghost-button" type="button" onClick={() => navigate(-1)}>
          Voltar
        </button>
        {isArchived ? (
          <button className="mac-button" type="button" onClick={handleRestore}>
            Restaurar medicamento
          </button>
        ) : (
          <button className="mac-button ghost-danger" type="button" onClick={handleSoftDelete}>
            Excluir medicamento
          </button>
        )}
      </div>

      {showAdjustmentModal && (
        <div className="modal-overlay" onClick={closeAdjustmentModal}>
          <div className="modal-content movement-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingMovementId ? 'Editar movimentação' : 'Nova movimentação'}</h2>
            </div>
            <form className="inventory-form" onSubmit={handleAddAdjustment}>
              <div className="form-grid inventory-grid">
                <label>
                  Tipo de movimento
                  <select name="type" className="mac-input" value={adjustmentData.type} onChange={handleAdjustmentChange}>
                    <option value="Entrada">Entrada</option>
                    <option value="Saída">Saída</option>
                  </select>
                </label>
                <label>
                  Paciente/Origem
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="patient"
                      className="mac-input"
                      placeholder={isExitMovement ? 'Digite o nome do paciente' : 'Nome do fornecedor'}
                      value={isExitMovement ? patientQuery : adjustmentData.patient}
                      onChange={(event) => {
                        if (isExitMovement) {
                          setPatientQuery(event.target.value);
                          setShowPatientDropdown(true);
                          setAdjustmentData((prev) => ({ ...prev, patient: '', patientId: '' }));
                        } else {
                          handleAdjustmentChange(event);
                        }
                      }}
                      onFocus={() => isExitMovement && setShowPatientDropdown(true)}
                      onBlur={() => {
                        if (isExitMovement) {
                          setTimeout(() => setShowPatientDropdown(false), 150);
                        }
                      }}
                      required
                    />
                    {isExitMovement && patientQuery && showPatientDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          right: 0,
                          background: '#fff',
                          border: '1px solid rgba(60,60,67,0.15)',
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                          maxHeight: '220px',
                          overflowY: 'auto',
                          zIndex: 20,
                        }}
                      >
                        {patients
                          .filter((patient) => patient?.name?.toLowerCase().includes(patientQuery.toLowerCase()))
                          .slice(0, 6)
                          .map((patient) => (
                            <button
                              type="button"
                              key={patient._id}
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                setAdjustmentData((prev) => ({
                                  ...prev,
                                  patient: patient.name,
                                  patientId: patient._id || patient.id || '',
                                }));
                                setPatientQuery(patient.name);
                                setShowPatientDropdown(false);
                              }}
                            >
                              <div style={{ fontWeight: 600 }}>{patient.name}</div>
                              {patient.cpf && (
                                <small style={{ color: '#6e6e73' }}>CPF: {patient.cpf}</small>
                              )}
                            </button>
                          ))}
                        {patients.filter((patient) => patient?.name?.toLowerCase().includes(patientQuery.toLowerCase())).length === 0 && (
                          <div style={{ padding: '12px', color: '#8e8e93', fontSize: '0.9rem' }}>
                            Nenhum paciente encontrado
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </label>
                <label>
                  Quantidade
                  <input
                    type="number"
                    min="0"
                    name="quantity"
                    className="mac-input"
                    value={adjustmentData.quantity}
                    onChange={handleAdjustmentChange}
                    required
                  />
                </label>
              </div>
              <div className="form-grid inventory-grid">
                <label>
                  Validade
                  <input
                    type="date"
                    name="expiry"
                    className="mac-input"
                    value={adjustmentData.expiry}
                    onChange={handleAdjustmentChange}
                    required
                  />
                </label>
                <label>
                  Lote
                  <input
                    type="text"
                    name="lot"
                    className="mac-input"
                    placeholder="Ex: DP2303"
                    value={adjustmentData.lot}
                    onChange={handleAdjustmentChange}
                    required
                  />
                </label>
                <label>
                  Observação
                  <input
                    type="text"
                    name="note"
                    className="mac-input"
                    placeholder="Ex: Lote, justificativa..."
                    value={adjustmentData.note}
                    onChange={handleAdjustmentChange}
                  />
                </label>
              </div>

              {adjustmentData.type === 'Entrada' && !editingMovementId && (
                <div className="payment-section">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="addPayment"
                      checked={addPayment}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setAddPayment(checked);
                        if (!checked) {
                          setPaymentData(defaultPaymentData);
                        }
                      }}
                    />
                    Registrar pagamento desta entrada
                  </label>
                  {addPayment && (
                    <>
                      <div className="form-grid inventory-grid">
                        <label>
                          Valor (R$)
                          <input
                            type="text"
                            name="amount"
                            className="mac-input"
                            value={paymentData.amount}
                            onChange={handlePaymentChange}
                            placeholder="0,00"
                            required
                          />
                        </label>
                        <label>
                          Data do pagamento
                          <input
                            type="date"
                            name="date"
                            className="mac-input"
                            value={paymentData.date}
                            onChange={handlePaymentChange}
                            required
                          />
                        </label>
                        <label>
                          Status
                          <select
                            name="paymentStatus"
                            className="mac-input"
                            value={paymentData.paymentStatus}
                            onChange={handlePaymentChange}
                            required
                          >
                            <option value="pendente">Pendente</option>
                            <option value="pago">Pago</option>
                            <option value="atrasado">Atrasado</option>
                          </select>
                        </label>
                      </div>
                      <div className="form-grid inventory-grid">
                        <label>
                          Forma de pagamento
                          <select
                            name="paymentMethod"
                            className="mac-input"
                            value={paymentData.paymentMethod}
                            onChange={handlePaymentChange}
                            required
                          >
                            <option value="dinheiro">Dinheiro</option>
                            <option value="cartao_credito">Cartão de crédito</option>
                            <option value="cartao_debito">Cartão de débito</option>
                            <option value="pix">PIX</option>
                            <option value="transferencia">Transferência</option>
                            <option value="boleto">Boleto</option>
                            <option value="outro">Outro</option>
                          </select>
                        </label>
                        <label>
                          <span>Pagamento parcelado</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              name="hasInstallments"
                              checked={paymentData.hasInstallments}
                              onChange={handlePaymentChange}
                            />
                            <span>Habilitar parcelamento</span>
                          </div>
                        </label>
                        {paymentData.hasInstallments && (
                          <>
                            <label>
                              Número de parcelas
                              <input
                                type="number"
                                min="1"
                                max="12"
                                name="installments"
                                className="mac-input"
                                value={paymentData.installments}
                                onChange={handlePaymentChange}
                                required
                              />
                            </label>
                            <label>
                              Método das parcelas
                              <select
                                name="installmentPaymentMethod"
                                className="mac-input"
                                value={paymentData.installmentPaymentMethod}
                                onChange={handlePaymentChange}
                                required
                              >
                                <option value="cartao_credito">Cartão de crédito</option>
                                <option value="cartao_debito">Cartão de débito</option>
                                <option value="pix">PIX</option>
                                <option value="transferencia">Transferência</option>
                                <option value="boleto">Boleto</option>
                                <option value="dinheiro">Dinheiro</option>
                                <option value="outro">Outro</option>
                              </select>
                            </label>
                          </>
                        )}
                      </div>
                      <label>
                        Observações do pagamento
                        <textarea
                          name="notes"
                          className="mac-input"
                          value={paymentData.notes}
                          onChange={handlePaymentChange}
                          rows={3}
                        />
                      </label>

                      {paymentData.hasInstallments && (
                        <div className="installment-preview">
                          <div className="installment-preview-header">
                            <h4>Lançamentos gerados</h4>
                            <button type="button" className="ghost-button" onClick={recalculateInstallments}>
                              Recalcular parcelas
                            </button>
                          </div>
                          {installmentSchedule.length === 0 ? (
                            <p className="helper-text" style={{ textAlign: 'left' }}>
                              Informe o valor, parcelas e data para gerar os lançamentos.
                            </p>
                          ) : (
                            <div className="installment-rows">
                              {installmentSchedule.map((inst, index) => (
                                <div className="installment-row" key={`${inst.installmentNumber || index}-${index}`}>
                                  <div className="installment-title">
                                    <strong>
                                      Parcela {inst.installmentNumber || index + 1}/{scheduleCount}
                                    </strong>
                                    <small>{formatCurrency(parseAmountToNumber(inst.amount))}</small>
                                  </div>
                                  <label>
                                    Data
                                    <input
                                      type="date"
                                      className="mac-input"
                                      value={inst.date}
                                      onChange={(event) =>
                                        handleInstallmentFieldChange(index, 'date', event.target.value)
                                      }
                                    />
                                  </label>
                                  <label>
                                    Valor
                                    <input
                                      type="text"
                                      className="mac-input"
                                      value={inst.amount}
                                      onChange={(event) =>
                                        handleInstallmentFieldChange(index, 'amount', formatCurrencyInput(event.target.value))
                                      }
                                    />
                                  </label>
                                  <label>
                                    Forma
                                    <select
                                      className="mac-input"
                                      value={inst.paymentMethod}
                                      onChange={(event) =>
                                        handleInstallmentFieldChange(index, 'paymentMethod', event.target.value)
                                      }
                                    >
                                      <option value="cartao_credito">Cartão de crédito</option>
                                      <option value="cartao_debito">Cartão de débito</option>
                                      <option value="pix">PIX</option>
                                      <option value="transferencia">Transferência</option>
                                      <option value="boleto">Boleto</option>
                                      <option value="dinheiro">Dinheiro</option>
                                      <option value="outro">Outro</option>
                                    </select>
                                  </label>
                                  <label>
                                    Status
                                    <select
                                      className="mac-input"
                                      value={inst.paymentStatus}
                                      onChange={(event) =>
                                        handleInstallmentFieldChange(index, 'paymentStatus', event.target.value)
                                      }
                                    >
                                      <option value="pendente">Pendente</option>
                                      <option value="pago">Pago</option>
                                      <option value="atrasado">Atrasado</option>
                                    </select>
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button className="ghost-button" type="button" onClick={closeAdjustmentModal}>
                  Cancelar
                </button>
                <button className="mac-button" type="submit">
                  {editingMovementId ? 'Salvar alterações' : (addPayment && adjustmentData.type === 'Entrada' ? 'Adicionar pagamento' : 'Registrar ajuste')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
